import { HttpErrorResponse } from '@angular/common/http';
import { throwError, Observable } from 'rxjs';
import { ApiError } from '../dto/api-error';

/**
 * Extracts ApiError from HttpErrorResponse and returns it as an Observable error.
 * 
 * This utility function is designed to be used with the RxJS catchError operator
 * in HTTP request pipes. It standardizes error handling by ensuring that all
 * HTTP errors are transformed into the ApiError format expected by the application.
 * 
 * @param error - The HttpErrorResponse from an Angular HTTP call
 * @returns Observable that errors with the ApiError object
 * 
 * @example
 * ```typescript
 * this.http.get<SomeData>('/api/endpoint').pipe(
 *   catchError(handleHttpError)
 * )
 * ```
 */
export function handleGenericHttpError(error: HttpErrorResponse): Observable<never> {
  // If the error response body is already an ApiError, use it
  if (error.error && isApiError(error.error)) {
    return throwError(() => error.error as ApiError);
  }

  // Otherwise, create a fallback ApiError structure
  const fallbackError: ApiError = {
    timestamp: new Date().toISOString(),
    status: error.status || 0,
    error: error.error || 'Unknown Error',
    message: error.message || 'An unexpected error occurred',
    path: error.url || '',
    fieldErrors: null
  };

  return throwError(() => fallbackError);
}

/**
 * Type guard to check if an object matches the ApiError interface structure.
 * 
 * @param obj - The object to check
 * @returns true if the object has all required ApiError properties
 */
function isApiError(obj: any): obj is ApiError {
  return (
    obj &&
    typeof obj.timestamp === 'string' &&
    typeof obj.status === 'number' &&
    typeof obj.error === 'string' &&
    typeof obj.message === 'string' &&
    typeof obj.path === 'string'
  );
}
